import * as THREE from "three";
import { Engine } from "../../game/engine";
import { AudioBed } from "../../game/audio";

/**
 * Vortex — first-person hex tunnel; flip gravity to dodge holes
 * and chain flips for combo multipliers.
 * Game 16 of platform games + 6-game rebrand pack (MVP / v0).
 *
 * Design dossier: docs/dossier-games/16-vortex/
 * Source IP: Tube / Tempest tunnel-shooter canon, plus the flip
 * mechanic from VVVVVV inverted into 6-fold rotational symmetry.
 *
 * MVP scope:
 *   - Hex tunnel: 6 walls (wall_id ∈ 0..5)
 *   - Deterministic hole schedule from seed (one bitmask per segment)
 *   - Auto-forward at AUTO_RUN_SPEED on whichever wall is "floor"
 *   - ←/→ flips gravity to adjacent wall (snap; v2 animates 60°)
 *   - Standing on a wall whose segment has a hole at that wall = fall
 *   - Flip chain within FLIP_COMBO_WINDOW_MS bumps multiplier (cap 5)
 *   - Score = floor(distance) + 50 × Σ multiplier_at_flip
 *   - 90 s run cap; fall ends run early
 *
 * v5 additions:
 *   - 60° animated camera roll on flip (FLIP_LERP_MS) — done
 *   - Per-frame engine.setBoost driven by combo level — done in v2(a)
 *   - Hole-density escalation ramps with depth — done in v2(b)
 *
 * Still deferred:
 *   - Biome cycling visuals
 *   - Tilt input on mobile (DeviceOrientation)
 *   - Per-segment trace beyond flip events
 */

// ============================================================
// Constants — must stay byte-identical with scoring-vortex.ts
// ============================================================

export const RUN_MS = 90_000;
export const HEX_WALLS = 6;
export const AUTO_RUN_SPEED = 24.0;
export const SEGMENT_LENGTH = 8.0;
export const TUNNEL_LENGTH_SEGMENTS = 320;
export const HOLE_RADIUS = 1.2;
export const FLIP_COMBO_WINDOW_MS = 1500;
export const MAX_COMBO_MULTIPLIER = 5;
export const FLIP_BONUS_PER_LEVEL = 50;
export const MAX_FLIPS = 600;
export const HOLE_DENSITY = 0.18;
export const HOLE_DENSITY_END = 0.36;
export const SAFE_PREFIX_SEGMENTS = 4;
export const RING_INTERVAL_SEGMENTS = 16;
export const RING_FIRST_SEGMENT = 8;
export const RING_BONUS = 200;

const TUNNEL_RADIUS = 4.0;
const FLIP_COOLDOWN_MS = 120;
/** v5: camera-roll lerp duration. Dossier-spec'd as FLIP_FREEZE_MS = 60
 *  but 180 ms reads as a clear-but-fast 60° pivot in playtest — short
 *  enough that a fast flipper isn't held back, long enough to register
 *  as motion rather than a hard snap. Pure visual; no scoring impact. */
const FLIP_LERP_MS = 180;

// ============================================================
// Tunnel topology — byte-identical with scoring-vortex
// ============================================================

function makeRng(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export type HexWallId = 0 | 1 | 2 | 3 | 4 | 5;

export interface TunnelSegment {
  idx: number;
  holes: number;
}

export interface RingSpawn {
  idx: number;
  segment_idx: number;
  wall: HexWallId;
}

export function generateRingSchedule(seed: number, tunnel: TunnelSegment[]): RingSpawn[] {
  const rng = makeRng(seed ^ 0x52494e47);
  const out: RingSpawn[] = [];
  let idx = 0;
  for (let segIdx = RING_FIRST_SEGMENT; segIdx < tunnel.length; segIdx += RING_INTERVAL_SEGMENTS) {
    const seg = tunnel[segIdx];
    const safe: HexWallId[] = [];
    for (let w = 0; w < HEX_WALLS; w++) {
      if ((seg.holes & (1 << w)) === 0) safe.push(w as HexWallId);
    }
    if (safe.length === 0) continue;
    const wall = safe[Math.floor(rng() * safe.length)];
    out.push({ idx, segment_idx: segIdx, wall });
    idx += 1;
  }
  return out;
}

export function generateTunnel(seed: number): TunnelSegment[] {
  const rng = makeRng(seed ^ 0x564f5258);
  const out: TunnelSegment[] = [];
  const rampSpan = TUNNEL_LENGTH_SEGMENTS - SAFE_PREFIX_SEGMENTS;
  for (let i = 0; i < TUNNEL_LENGTH_SEGMENTS; i++) {
    let holes = 0;
    if (i >= SAFE_PREFIX_SEGMENTS) {
      const t = (i - SAFE_PREFIX_SEGMENTS) / Math.max(1, rampSpan - 1);
      const density = HOLE_DENSITY + (HOLE_DENSITY_END - HOLE_DENSITY) * t;
      for (let w = 0; w < HEX_WALLS; w++) {
        if (rng() < density) holes |= (1 << w);
      }
      if (holes === 0b111111) {
        const safe = Math.floor(rng() * HEX_WALLS) & 0b111;
        holes &= ~(1 << safe);
      }
    }
    out.push({ idx: i, holes });
  }
  return out;
}

// ============================================================
// Types
// ============================================================

export interface FlipEvent { t: number; to: HexWallId }
export interface FallEvent { t: number; segment_idx: number; wall: HexWallId }

export interface VortexResult {
  distance_m: number;
  flips: number;
  peak_combo: number;
  rings_collected: number;
  fell: boolean;
  duration_ms: number;
  flipEvents: FlipEvent[];
  fallEvent: FallEvent | null;
  endT: number;
  scoreEstimate: number;
}

export interface VortexCallbacks {
  onHud: (runMs: number, distance: number, wall: HexWallId, combo: number, scoreEstimate: number) => void;
  onGameOver: (result: VortexResult) => void;
}

// ============================================================
// Game class
// ============================================================

export class Vortex {
  private engine: Engine;
  private audio = new AudioBed();
  private tunnel: TunnelSegment[];
  private rings: RingSpawn[] = [];
  private ringMeshBySegment = new Map<number, THREE.Mesh>();
  private ringsCollected = 0;
  private collectedRingIdxs = new Set<number>();
  // v6 — Tempest-canon interior accent rings every Nth segment.
  // These are emissive hex outlines that read as the tunnel "pulsing"
  // around the camera as combo ramps. Per memory
  // feedback-sibling-games-shallow §"real 3D scene with depth cues".
  private accentRings: THREE.LineSegments[] = [];

  // Tunnel mesh: one Group per segment with 6 wall panels.
  private segmentGroups: THREE.Group[] = [];

  // Camera rig: lives at (0,0,bz) and is rolled around the Z axis so
  // the "floor" wall appears below the camera. v5: the roll lerps
  // toward target over FLIP_LERP_MS instead of snapping.
  private cameraRig: THREE.Object3D;
  private cameraRollTarget = 0;   // accumulated radians (signed, never wrapped)
  private cameraRollCurrent = 0;

  // State
  private bz = 0;
  private currentWall: HexWallId = 0;
  private startedAt = 0;
  private lastFlipT = -Infinity;
  private lastFlipPerformedAt = -Infinity;
  private combo = 0;
  private peakCombo = 0;
  private flips: FlipEvent[] = [];
  private fall: FallEvent | null = null;
  private scoreEstimate = 0;
  private gameOver = false;
  private cb: VortexCallbacks;
  private inputLeft = false;
  private inputRight = false;

  private onKeyDown = (e: KeyboardEvent) => {
    if (e.code === "ArrowLeft" || e.code === "KeyA") { if (!this.inputLeft) { this.inputLeft = true; this.tryFlip(-1); } }
    if (e.code === "ArrowRight" || e.code === "KeyD") { if (!this.inputRight) { this.inputRight = true; this.tryFlip(+1); } }
  };
  private onKeyUp = (e: KeyboardEvent) => {
    if (e.code === "ArrowLeft" || e.code === "KeyA") this.inputLeft = false;
    if (e.code === "ArrowRight" || e.code === "KeyD") this.inputRight = false;
  };

  constructor(canvas: HTMLCanvasElement, cb: VortexCallbacks, seed: number) {
    this.cb = cb;
    this.tunnel = generateTunnel(seed);

    this.engine = new Engine(canvas, { onFrame: (dt) => this.frame(dt) });
    this.engine.scene.background = new THREE.Color(0x080418);
    this.engine.scene.fog = new THREE.Fog(0x080418, 16, 96);
    this.engine.scene.add(new THREE.AmbientLight(0x402040, 0.5));
    const fwd = new THREE.PointLight(0xff60ff, 1.5, 40, 1.6);
    fwd.position.set(0, 0, 8);
    this.engine.scene.add(fwd);
    const trail = new THREE.PointLight(0x66aaff, 0.8, 32, 1.4);
    trail.position.set(0, 0, -6);
    this.engine.scene.add(trail);

    this.cameraRig = new THREE.Object3D();
    this.engine.scene.add(this.cameraRig);
    this.engine.camera.position.set(0, 0, 0);
    this.engine.camera.lookAt(0, 0, 8);
    this.engine.camera.fov = 80;
    this.engine.camera.near = 0.1;
    this.engine.camera.far = 200;
    this.engine.camera.updateProjectionMatrix();
    this.cameraRig.add(this.engine.camera);

    this.buildTunnel();
    this.rings = generateRingSchedule(seed, this.tunnel);
    this.buildRings();
    window.addEventListener("keydown", this.onKeyDown);
    window.addEventListener("keyup", this.onKeyUp);
    this.mountTouch();
  }

  /** Mobile: split-screen tap zones. Left zone → flip CCW; right zone →
   *  flip CW. Each tap is one edge-trigger event, mirroring the keyboard
   *  arrow's on-press semantics. FLIP_COOLDOWN_MS in tryFlip() throttles
   *  rapid taps; cooldown is also enforced server-side. */
  private touchLeftEl: HTMLElement | null = null;
  private touchRightEl: HTMLElement | null = null;
  private onTouchLeft = (e: Event) => { e.preventDefault(); this.tryFlip(-1); };
  private onTouchRight = (e: Event) => { e.preventDefault(); this.tryFlip(+1); };
  private mountTouch() {
    this.touchLeftEl = document.getElementById("vortex-touch-left");
    this.touchRightEl = document.getElementById("vortex-touch-right");
    if (this.touchLeftEl) this.touchLeftEl.addEventListener("pointerdown", this.onTouchLeft);
    if (this.touchRightEl) this.touchRightEl.addEventListener("pointerdown", this.onTouchRight);
  }
  private unmountTouch() {
    if (this.touchLeftEl) this.touchLeftEl.removeEventListener("pointerdown", this.onTouchLeft);
    if (this.touchRightEl) this.touchRightEl.removeEventListener("pointerdown", this.onTouchRight);
  }

  /** Build the full tunnel as ~ 320 segment-groups. Each segment has
   *  6 wall panels; walls with a hole get a darker emissive material
   *  with a hole-circle cut out (we just thin the wall to alpha for
   *  MVP — a real circle cut needs custom shader). */
  private buildTunnel() {
    const segL = SEGMENT_LENGTH;
    const apothem = TUNNEL_RADIUS;
    // hex side length s = 2 * apothem / sqrt(3)
    const sideLen = (2 * apothem) / Math.sqrt(3);
    const wallGeo = new THREE.PlaneGeometry(sideLen, segL);

    const solidMat = new THREE.MeshStandardMaterial({
      color: 0x1a1a3a, emissive: 0x6a26ff, emissiveIntensity: 0.55,
      roughness: 0.5, metalness: 0.3, side: THREE.DoubleSide,
    });
    const holeMat = new THREE.MeshStandardMaterial({
      color: 0x0a0512, emissive: 0xff2a6a, emissiveIntensity: 0.9,
      roughness: 0.7, metalness: 0.1, side: THREE.DoubleSide,
      transparent: true, opacity: 0.35,
    });

    for (const seg of this.tunnel) {
      const g = new THREE.Group();
      g.position.set(0, 0, seg.idx * segL);
      for (let w = 0; w < HEX_WALLS; w++) {
        const has_hole = (seg.holes & (1 << w)) !== 0;
        const m = new THREE.Mesh(wallGeo, has_hole ? holeMat : solidMat);
        // Each wall sits at angle w × 60° around the Z axis, at distance apothem.
        // The "floor" wall at wall_id=0 is at angle -90° (below the camera).
        const ang = -Math.PI / 2 + (w * Math.PI) / 3;
        m.position.set(apothem * Math.cos(ang), apothem * Math.sin(ang), segL * 0.5);
        // Rotate the plane so its normal points inward.
        m.lookAt(0, 0, m.position.z);
        g.add(m);
      }
      this.engine.scene.add(g);
      this.segmentGroups.push(g);
    }

    // v6 — Tempest-canon interior accent rings. Every 6th segment gets
    // a bright hex outline at the segment seam: 6 corners on a circle
    // of radius cornerR = apothem * 2/√3, connected by LineSegments
    // (12 vertices = 6 edges × 2 endpoints). The emissive cyan reads
    // as a pulse marker as the camera flies through, giving the
    // tunnel metered depth instead of one continuous extrusion.
    // Bloom (always on at strength 0.7) makes them glow for free.
    const accentMat = new THREE.LineBasicMaterial({ color: 0x40e8ff, transparent: true, opacity: 0.85 });
    const cornerR = (apothem * 2) / Math.sqrt(3);
    // Pre-compute the 6 corner positions (relative to segment center).
    const corners: Array<[number, number]> = [];
    for (let c = 0; c < HEX_WALLS; c++) {
      const a = -Math.PI / 2 - Math.PI / 6 + (c * Math.PI) / 3;
      corners.push([cornerR * Math.cos(a), cornerR * Math.sin(a)]);
    }
    for (let i = 0; i < this.tunnel.length; i += 6) {
      const seg = this.tunnel[i];
      const vertices: number[] = [];
      for (let c = 0; c < HEX_WALLS; c++) {
        const [x0, y0] = corners[c];
        const [x1, y1] = corners[(c + 1) % HEX_WALLS];
        vertices.push(x0, y0, 0, x1, y1, 0);
      }
      const geo = new THREE.BufferGeometry();
      geo.setAttribute("position", new THREE.Float32BufferAttribute(vertices, 3));
      const line = new THREE.LineSegments(geo, accentMat);
      line.position.set(0, 0, seg.idx * segL);
      this.engine.scene.add(line);
      this.accentRings.push(line);
    }
  }

  /** Phase 2: build a golden ring on each scheduled ring's wall +
   *  segment. The ring is positioned where the player must be to
   *  collect it; visually pulses to attract attention. */
  private buildRings() {
    const segL = SEGMENT_LENGTH;
    const apothem = TUNNEL_RADIUS;
    const ringGeo = new THREE.TorusGeometry(0.7, 0.13, 8, 22);
    const ringMat = new THREE.MeshStandardMaterial({
      color: 0xffd54a, emissive: 0xffd54a, emissiveIntensity: 1.2,
      roughness: 0.3, metalness: 0.6,
    });
    for (const ring of this.rings) {
      const ang = -Math.PI / 2 + (ring.wall * Math.PI) / 3;
      const m = new THREE.Mesh(ringGeo, ringMat);
      m.position.set(
        apothem * 0.65 * Math.cos(ang),
        apothem * 0.65 * Math.sin(ang),
        ring.segment_idx * segL + segL * 0.5,
      );
      // Orient ring face toward camera (along z).
      m.rotation.x = Math.PI / 2;
      this.engine.scene.add(m);
      this.ringMeshBySegment.set(ring.segment_idx, m);
    }
  }

  private tryFlip(dir: -1 | 1) {
    const now = performance.now();
    const runMs = now - this.startedAt;
    if (runMs < 0 || this.gameOver) return;
    if (now - this.lastFlipPerformedAt < FLIP_COOLDOWN_MS) return;
    const nextWall = ((this.currentWall + dir + HEX_WALLS) % HEX_WALLS) as HexWallId;
    if (runMs - this.lastFlipT < FLIP_COMBO_WINDOW_MS) {
      this.combo = Math.min(MAX_COMBO_MULTIPLIER, this.combo + 1);
    } else {
      this.combo = 1;
    }
    this.lastFlipT = runMs;
    this.lastFlipPerformedAt = now;
    if (this.combo > this.peakCombo) this.peakCombo = this.combo;
    this.currentWall = nextWall;
    if (this.flips.length < MAX_FLIPS) {
      this.flips.push({ t: Math.round(runMs), to: nextWall });
    }
    this.scoreEstimate += FLIP_BONUS_PER_LEVEL * this.combo;
    // Audio cue: chime, pitched by combo level.
    try { this.audio.chime(this.combo * 80); } catch { /* audio off */ }
    // v5: lerp camera roll over FLIP_LERP_MS instead of snapping.
    // Accumulate ±60° on the target so consecutive same-direction
    // flips chain into a smooth continuous spin past 360°.
    this.cameraRollTarget += dir === 1 ? -Math.PI / 3 : Math.PI / 3;
  }

  private frame(dt: number) {
    if (this.gameOver) return;
    const now = performance.now();
    const runMs = now - this.startedAt;
    if (runMs >= RUN_MS) { this.endRun(runMs); return; }

    // Boost intensity = combo level. Classic chain-rewards-visual loop:
    // higher combo → tunnel walls pulse, chromatic edge tint, motion blur.
    // (depth-bar post-processing)
    const comboNorm = this.combo / MAX_COMBO_MULTIPLIER;
    this.engine.setBoost(comboNorm, dt);

    // v6 — adaptive audio drone tracks the same combo signal so the
    // bed thickens audibly as the chain builds. Per memory
    // feedback-sibling-games-shallow §"at least one escalation hook".
    // Capped at 0.7. AudioBed.setDroneIntensity damps internally so
    // per-frame setting is cheap.
    try { this.audio.setDroneIntensity(0.15 + comboNorm * 0.55); } catch { /* off */ }

    // Advance forward.
    this.bz += AUTO_RUN_SPEED * dt;
    this.cameraRig.position.set(0, 0, this.bz);

    // v5: camera-roll lerp toward target at FLIP_LERP_MS pace.
    if (this.cameraRollCurrent !== this.cameraRollTarget) {
      const diff = this.cameraRollTarget - this.cameraRollCurrent;
      const speed = Math.PI / 3 / (FLIP_LERP_MS / 1000);  // rad/s
      const step = Math.sign(diff) * Math.min(Math.abs(diff), speed * dt);
      this.cameraRollCurrent += step;
      this.cameraRig.rotation.z = this.cameraRollCurrent;
    }

    // Check current segment for fall.
    const segIdx = Math.floor(this.bz / SEGMENT_LENGTH);
    if (segIdx < this.tunnel.length) {
      const seg = this.tunnel[segIdx];
      if ((seg.holes & (1 << this.currentWall)) !== 0) {
        this.fall = { t: Math.round(runMs), segment_idx: segIdx, wall: this.currentWall };
        this.endRun(runMs);
        return;
      }
      // Phase 2: ring collect — same wall as ring's wall = +RING_BONUS.
      const ring = this.rings.find(r => r.segment_idx === segIdx);
      if (ring && !this.collectedRingIdxs.has(ring.idx) && ring.wall === this.currentWall) {
        this.collectedRingIdxs.add(ring.idx);
        this.ringsCollected += 1;
        this.scoreEstimate += RING_BONUS;
        const m = this.ringMeshBySegment.get(segIdx);
        if (m) m.visible = false;
        try { this.audio.chime(220); } catch { /* off */ }
      }
    }

    // Combo decay: if the window has passed since last flip, reset.
    if (runMs - this.lastFlipT >= FLIP_COMBO_WINDOW_MS) {
      this.combo = 0;
    }

    this.cb.onHud(runMs, this.bz, this.currentWall, this.combo, Math.floor(this.scoreEstimate + this.bz));
  }

  private endRun(runMs: number) {
    this.gameOver = true;
    // v6 — quiet the adaptive drone so it doesn't bleed into game-over.
    try { this.audio.setDroneIntensity(0); } catch { /* off */ }
    const result: VortexResult = {
      distance_m: Math.round(this.bz * 10) / 10,
      flips: this.flips.length,
      peak_combo: this.peakCombo,
      rings_collected: this.ringsCollected,
      fell: this.fall !== null,
      duration_ms: Math.round(runMs),
      flipEvents: this.flips,
      fallEvent: this.fall,
      endT: Math.round(runMs),
      scoreEstimate: Math.floor(this.scoreEstimate + this.bz),
    };
    this.cb.onGameOver(result);
  }

  start() {
    this.startedAt = performance.now();
    this.engine.start();
  }
  armAudio() { try { this.audio.arm(); } catch { /* gesture-gated */ } }
  setVolume(v: number) { this.audio.setVolume(v); }
  setMuted(m: boolean) { this.audio.setMuted(m); }
  dispose() {
    this.gameOver = true;
    try { this.audio.setDroneIntensity(0); } catch { /* off */ }
    // v6 — drop the accent rings.
    for (const ring of this.accentRings) {
      this.engine.scene.remove(ring);
      ring.geometry.dispose();
      (ring.material as THREE.Material).dispose();
    }
    this.accentRings = [];
    this.engine.stop();
    window.removeEventListener("keydown", this.onKeyDown);
    window.removeEventListener("keyup", this.onKeyUp);
    this.unmountTouch();
  }
}
